import { useId, useState } from 'react'
import { NavLink, matchPath, useLocation, useNavigate } from 'react-router'
import { ChevronRight, Lock, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  show: boolean
  /** Shown to everyone so they know it exists, but closed to this person: a lock on the row, and the page explains. */
  locked?: boolean
  /** A count shown on the row (what is waiting on you). Zero or absent shows nothing. */
  badge?: number
  /** Match this path exactly, so an index entry is not also lit on its child pages. */
  end?: boolean
  /** For entries that differ only by query string (the CRM sections): lit only when this returns true. */
  matchSearch?: (search: URLSearchParams) => boolean
  /**
   * Makes this entry a folder: in the full sidebar it expands to show these links instead of
   * navigating itself. Where there is no room to nest — the folded icon rail and the Dock — the
   * children stand in its place as ordinary entries (`flattenNavGroups`). The caller filters
   * children by `show` and drops a folder left with none.
   */
  children?: NavItem[]
}
export interface NavGroup {
  label: string
  items: NavItem[]
  /**
   * Long groups fold into ONE sidebar entry that unfolds their items inline, so the
   * rail stays short however many sections a group grows. The entry wears this icon.
   */
  drawer?: { icon: LucideIcon; blurb?: string }
}

const isItemActive = (item: NavItem, pathname: string, query: URLSearchParams) =>
  Boolean(matchPath({ path: item.to.split('?')[0] ?? item.to, end: item.end ?? false }, pathname)) && (!item.matchSearch || item.matchSearch(query))

/**
 * The sidebar entry for a long group: ONE row, closed by default. Clicking it goes straight to the
 * group's first page and unfolds its sections right under it, so there is no second click. While you
 * are inside the group the row toggles the list; elsewhere it navigates.
 */
function DrawerEntry({ group, collapsed, onNavigate }: { group: NavGroup; collapsed: boolean; onNavigate?: () => void }) {
  const { pathname, search } = useLocation()
  const query = new URLSearchParams(search)
  const navigate = useNavigate()
  const panelId = useId()
  const visible = group.items.filter((item) => item.show)
  const Icon = group.drawer!.icon
  const active = visible.some((item) => isItemActive(item, pathname, query) || (item.children ?? []).some((child) => child.show && isItemActive(child, pathname, query)))
  // What is waiting on you inside the group, so a closed group does not hide it.
  const waiting = visible.reduce((sum, item) => sum + (item.badge ?? 0), 0)
  const [open, setOpen] = useState(active)
  const [wasActive, setWasActive] = useState(active)
  // Arriving inside the group (a link, a refresh) unfolds it. Adjusted during render, not in an effect.
  if (wasActive !== active) {
    setWasActive(active)
    setOpen(active)
  }
  if (visible.length === 0) return null
  const first = visible[0]!.children?.find((child) => child.show) ?? visible[0]!
  const onClick = () => {
    if (!active) {
      navigate(first.to)
      setOpen(true)
      onNavigate?.()
      return
    }
    if (!collapsed) setOpen((value) => !value)
  }
  const unfolded = open && !collapsed
  return (
    <div className="flex flex-col">
      <button
        type="button"
        aria-label={collapsed ? group.label : undefined}
        aria-expanded={collapsed ? undefined : unfolded}
        aria-controls={collapsed ? undefined : panelId}
        title={collapsed ? group.label : undefined}
        onClick={onClick}
        className={cn(
          ROW,
          'w-full text-left',
          collapsed && 'justify-center gap-0 px-0',
          active && !unfolded ? ROW_ACTIVE : active ? 'font-semibold text-white hover:bg-nav-hover' : ROW_IDLE,
        )}
      >
        <Icon className={cn('size-[18px] shrink-0', active ? 'text-white' : 'text-nav-icon group-hover:text-white')} />
        {collapsed ? null : (
          <>
            <span className="flex-1">{group.label}</span>
            {waiting > 0 && !unfolded ? (
              <span className="num rounded-full bg-brand-accent px-1.5 text-micro font-semibold text-white" aria-label={`${waiting} waiting`}>{waiting}</span>
            ) : (
              <span className="num rounded-full bg-white/10 px-1.5 text-micro text-nav-text">{visible.length}</span>
            )}
            <ChevronRight aria-hidden className={cn('size-4 shrink-0 text-nav-icon transition-transform duration-200 ease-out', unfolded && 'rotate-90')} />
          </>
        )}
      </button>
      {unfolded ? (
        <div id={panelId} className="ml-[1.375rem] mt-1 flex flex-col gap-0.5 border-l border-nav-line pl-2">
          {visible.map((item) =>
            item.children?.length ? (
              <SidebarFolder key={`folder:${item.label}`} item={item} onNavigate={onNavigate} />
            ) : (
              <SidebarLink key={item.to} item={item} onNavigate={onNavigate} nested />
            ),
          )}
        </div>
      ) : null}
    </div>
  )
}

/** Folders replaced by their children, for the layouts that cannot nest (the rail, the Dock). */
export function flattenNavGroups(groups: NavGroup[]): NavGroup[] {
  return groups.map((group) => ({ ...group, items: group.items.flatMap((item) => (item.children?.length ? item.children : [item])) }))
}

/** Shared by top-level links, folder headers and the links inside a folder. */
const ROW = 'group relative flex items-center gap-3 overflow-hidden whitespace-nowrap rounded-lg px-3 py-2.5 text-sm transition-[background-color,color,font-weight,transform,padding] duration-200 ease-out'
const ROW_IDLE = 'text-nav-text hover:bg-nav-hover hover:font-medium hover:text-white motion-safe:hover:translate-x-0.5'
const ROW_ACTIVE = 'bg-nav-active font-semibold text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]'

/**
 * The sidebar's sections: a small caps label, its links, and a rule between sections.
 * Used by the desktop panel and the mobile sheet so the two cannot drift apart.
 */
export function SidebarNav({ groups, onNavigate, className, collapsed = false }: { groups: NavGroup[]; onNavigate?: () => void; className?: string; collapsed?: boolean }) {
  // Folded to icons there is no room to nest, so a folder's children stand in for it.
  const shown = collapsed ? flattenNavGroups(groups) : groups
  return (
    <nav className={cn('flex flex-col', className)}>
      {shown.map((group, index) => (
        <div key={group.label} className={cn('flex flex-col gap-1 py-4 transition-[padding] duration-[420ms] ease-[var(--tt-ease-ios)]', collapsed ? 'px-2.5' : 'px-3', index > 0 && 'border-t border-nav-line')}>
          {/* The section label folds away with the sidebar; the rule between sections stays. */}
          <p
            aria-hidden={collapsed}
            className={cn(
              'overflow-hidden whitespace-nowrap px-3 text-micro font-semibold tracking-[0.08em] text-nav-label uppercase transition-[max-height,opacity,padding] duration-[420ms] ease-[var(--tt-ease-ios)]',
              collapsed ? 'max-h-0 pb-0 opacity-0' : 'max-h-6 pb-1.5 opacity-100',
            )}
          >
            {group.label}
          </p>
          {group.drawer ? (
            <DrawerEntry group={group} collapsed={collapsed} onNavigate={onNavigate} />
          ) : (
          group.items.map((item) =>
            item.children?.length ? (
              <SidebarFolder key={`folder:${item.label}`} item={item} onNavigate={onNavigate} />
            ) : (
              <SidebarLink key={item.to} item={item} onNavigate={onNavigate} collapsed={collapsed} />
            ),
          )
          )}
        </div>
      ))}
    </nav>
  )
}

function SidebarLink({ item, onNavigate, collapsed = false, nested = false }: { item: NavItem; onNavigate?: () => void; collapsed?: boolean; nested?: boolean }) {
  const { search } = useLocation()
  const query = new URLSearchParams(search)
  const { to, label, icon: Icon, end, matchSearch, badge, locked } = item
  return (
    <NavLink
      to={to}
      aria-label={collapsed ? label : undefined}
      title={locked ? `${label} is locked for you` : undefined}
      // The Daily diary note flies to this link when it is saved.
      data-diary-nav={label === 'Diary' ? '' : undefined}
      end={end}
      onClick={onNavigate}
      className={({ isActive: pathActive }) => {
        const isActive = pathActive && (!matchSearch || matchSearch(query))
        return cn(
          // Hover and active both read as "lit": white text, heavier weight, brighter icon.
          // The slide and icon pop are motion-safe only.
          ROW,
          nested && 'py-2 pl-4',
          collapsed && 'justify-center gap-0 px-0',
          isActive ? ROW_ACTIVE : ROW_IDLE,
          collapsed && 'motion-safe:hover:translate-x-0',
        )
      }}
    >
      {({ isActive: pathActive }) => {
        const isActive = pathActive && (!matchSearch || matchSearch(query))
        return (
        <>
          <span
            aria-hidden
            className={cn(
              'absolute inset-y-2 left-0 w-[3px] rounded-r-full bg-white transition-[opacity,transform] duration-200 ease-out',
              isActive ? 'opacity-100' : 'scale-y-50 opacity-0',
            )}
          />
          <Icon
            className={cn(
              'shrink-0 transition-[color,transform] duration-200 ease-out',
              nested ? 'size-4' : 'size-[18px]',
              isActive ? 'text-white' : 'text-nav-icon group-hover:text-white motion-safe:group-hover:scale-110',
            )}
          />
          <span
            className={cn(
              // Labels trail the width a beat on the way out of the rail and leave first on the way in.
              'transition-[opacity,max-width,transform] ease-[var(--tt-ease-ios)]',
              collapsed ? 'max-w-0 -translate-x-1 opacity-0 duration-150' : 'max-w-[12rem] translate-x-0 opacity-100 delay-75 duration-[420ms]',
            )}
          >
            {label}
          </span>
          {locked && !collapsed ? <Lock aria-label="Locked for you" className="ml-auto size-3.5 shrink-0 text-nav-icon" /> : null}
          {badge && badge > 0 && !collapsed ? <span className="num ml-auto rounded-full bg-brand-accent px-1.5 text-micro font-semibold text-white">{badge > 99 ? '99+' : badge}</span> : null}
        </>
        )
      }}
    </NavLink>
  )
}

/** Strip the query string so a child's `to` can be matched against the current pathname. */
function pathOf(to: string): string {
  return to.split('?')[0] ?? to
}

/**
 * An expandable entry that groups related pages under one name. Full-width sidebar only.
 *
 * It opens by itself whenever the current page is one of its children — arriving on Project
 * Timeline from a link should never leave the entry you are on hidden inside a closed folder —
 * and otherwise toggles on click.
 */
function SidebarFolder({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const { pathname } = useLocation()
  const children = item.children ?? []
  const containsCurrent = children.some((child) => matchPath({ path: pathOf(child.to), end: child.end ?? false }, pathname) != null)
  const [open, setOpen] = useState(containsCurrent)
  const [wasCurrent, setWasCurrent] = useState(containsCurrent)
  const panelId = useId()
  const Icon = item.icon

  // Arriving on a child opens the folder. Adjusted during render rather than in an effect,
  // so there is no frame where the current page sits hidden inside a closed folder.
  if (wasCurrent !== containsCurrent) {
    setWasCurrent(containsCurrent)
    if (containsCurrent) setOpen(true)
  }

  return (
    <div className="flex flex-col">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          ROW,
          'w-full text-left',
          // Closed over the current page: lit like a link, so where you are is never invisible.
          containsCurrent && !open ? ROW_ACTIVE : containsCurrent ? 'font-semibold text-white hover:bg-nav-hover' : ROW_IDLE,
        )}
      >
        <Icon
          className={cn(
            'size-[18px] shrink-0 transition-[color,transform] duration-200 ease-out',
            containsCurrent ? 'text-white' : 'text-nav-icon group-hover:text-white motion-safe:group-hover:scale-110',
          )}
        />
        <span className="flex-1">{item.label}</span>
        <ChevronRight
          aria-hidden
          className={cn('size-4 shrink-0 text-nav-icon transition-transform duration-200 ease-out', open && 'rotate-90')}
        />
      </button>
      {open ? (
        <div id={panelId} className="ml-[1.375rem] mt-1 flex flex-col gap-0.5 border-l border-nav-line pl-2">
          {children.map((child) => (
            <SidebarLink key={child.to} item={child} onNavigate={onNavigate} nested />
          ))}
        </div>
      ) : null}
    </div>
  )
}
